import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { restoreTestsQuery, saveRestoreResult, saveRestoreTests, type RestoreTestRow, type RestoreTestsPage } from '../../api/settingsProtectee'
import { hrefOf } from '../../overview/href'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings > Protectee > Restore tests (CyFun PR.DS-11.2): pornit, OD deschide
 * tichetul "Backup restore test" pe loc si apoi la fiecare perioada; inchis cu
 * un fisier atasat, e dovada pe tabloul Protectee si in NIS2. Sub calendar,
 * serverul restaurat si minutele: cardul "Restore tests that took longer than
 * the server's RTO" (GV.OC-04.4) le compara cu RTO-ul scris pe server.
 */
export function RestoreTestsPanel() {
  const page = useQuery(restoreTestsQuery())

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <div className="alert alert-danger">{page.error.message}</div>
  if (!page.data) return <Spinner />

  return (
    <div id="content">
      <header className="panel-heading" style={{ borderBottom: 0 }}>
        <h3>
          <strong>Restore</strong> tests
        </h3>
        <label className="color">a backup nobody has restored is a hope, not a copy</label>
      </header>
      <Body page={page.data} />
    </div>
  )
}

function Body({ page }: { page: RestoreTestsPage }) {
  if (!page.ready) {
    return <div className="alert alert-warning">The restore test tables are not installed yet. They come with the next database migration.</div>
  }
  if (page.company === null) {
    return <div className="alert alert-info">Pick one company in the header: the restore test schedule is per company.</div>
  }

  return (
    <>
      <RestoreForm page={page} />
      {page.measurable && <Measured page={page} />}
    </>
  )
}

function RestoreForm({ page }: { page: RestoreTestsPage }) {
  const client = useQueryClient()
  const [enabled, setEnabled] = useState(page.enabled)
  const [period, setPeriod] = useState(page.period)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setEnabled(page.enabled)
    setPeriod(page.period)
  }, [page.enabled, page.period])

  const dirty = enabled !== page.enabled || period !== page.period

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!page.canEdit || !dirty || busy) return
    setBusy(true)
    setError(null)
    try {
      const next = await saveRestoreTests(enabled, period)
      client.setQueryData(restoreTestsQuery().queryKey, next)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The schedule could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="restore-tests-form" noValidate onSubmit={(e) => void submit(e)}>
      <p>
        Company: <strong>{page.company?.name}</strong>
      </p>
      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            checked={enabled}
            disabled={!page.canEdit || busy}
            aria-describedby="restore-tests-help"
            onChange={(e) => {
              setEnabled(e.target.checked)
              setSaved(false)
            }}
          />{' '}
          Open a backup restore test ticket on a schedule
        </label>
      </div>
      <div className="form-group" style={{ maxWidth: 240 }}>
        <label htmlFor="restore-tests-period">Every</label>
        <select
          id="restore-tests-period"
          className="form-control"
          value={period}
          disabled={!page.canEdit || busy}
          onChange={(e) => {
            setPeriod(Number(e.target.value))
            setSaved(false)
          }}
        >
          {page.periods.map((months) => (
            <option key={months} value={months}>
              {months === 12 ? '12 months (yearly)' : months === 1 ? '1 month' : `${months} months`}
            </option>
          ))}
        </select>
      </div>
      <p id="restore-tests-help" className="text-muted" style={{ fontSize: 12 }}>
        When it is on, a ticket &quot;Backup restore test&quot; opens right away and then once per period, with the steps: restore a sample to a separate
        place, check it opens, attach the proof (a screenshot, the restore log). Closed with a file attached, it is the dated evidence the audit and NIS2
        (CyFun PR.DS-11.2) ask for. A new ticket does not open while the previous one is still open.
      </p>
      <LastTest page={page} />
      {error && <div className="alert alert-danger">{error}</div>}
      {saved && !error && <div className="alert alert-success">Saved.</div>}
      {page.canEdit && (
        <button type="submit" className="btn btn-primary" disabled={!dirty || busy} aria-label="Save restore tests">
          {busy ? 'Saving...' : 'Save'}
        </button>
      )}
    </form>
  )
}

function LastTest({ page }: { page: RestoreTestsPage }) {
  const last = page.last
  if (last === null) {
    return page.enabled ? <p className="text-muted">The first ticket opens within a minute.</p> : null
  }
  const state = !last.done ? 'still open' : last.files > 0 ? `closed with ${last.files} file${last.files > 1 ? 's' : ''} - proven` : 'closed without a file - not proof yet'

  return (
    <p>
      Last ticket:{' '}
      <a href={hrefOf(`easeedesk/ticket/${last.ticket_id}`) ?? '#'}>#{last.ticket_id}</a>, opened {last.opened_at.slice(0, 10)}, {state}.
      {page.enabledAt && <span className="text-muted"> On since {page.enabledAt.slice(0, 10)}.</span>}
    </p>
  )
}

function Measured({ page }: { page: RestoreTestsPage }) {
  return (
    <section aria-labelledby="measured-restores">
      <h4 id="measured-restores">Measured restores</h4>
      <p className="text-muted" style={{ fontSize: 12 }}>
        For each restore test, pick the server that was restored and write how many minutes it took. The audit and NIS2 (CyFun GV.OC-04.4) compare that
        with the recovery time objective written on the server. A test without a server or minutes is not measured - it does not count as a pass.
      </p>
      {page.servers.length === 0 && (
        <div className="alert alert-info">
          No server has a recovery time objective yet. Write &quot;Recovery time objective in hours&quot; on the server first, then it can be picked here.
        </div>
      )}
      {page.tests.length === 0 ? (
        <p className="text-muted">No restore test yet.</p>
      ) : (
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Opened</th>
              <th>Ticket</th>
              <th>Restored server</th>
              <th>Minutes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {page.tests.map((test) => (
              <ResultRow key={test.id} test={test} servers={page.servers} canEdit={page.canEdit} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function ResultRow({ test, servers, canEdit }: { test: RestoreTestRow; servers: { id: number; name: string }[]; canEdit: boolean }) {
  const client = useQueryClient()
  const day = test.opened_at.slice(0, 10)
  const [device, setDevice] = useState(test.device_id === null ? '' : String(test.device_id))
  const [minutes, setMinutes] = useState(test.duration_minutes === null ? '' : String(test.duration_minutes))
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setDevice(test.device_id === null ? '' : String(test.device_id))
    setMinutes(test.duration_minutes === null ? '' : String(test.duration_minutes))
  }, [test.device_id, test.duration_minutes])

  const dirty = device !== (test.device_id === null ? '' : String(test.device_id)) || minutes !== (test.duration_minutes === null ? '' : String(test.duration_minutes))

  const save = async () => {
    if (!dirty || busy) return
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      client.setQueryData(restoreTestsQuery().queryKey, await saveRestoreResult(test.id, device, minutes))
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The result could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const known = servers.some((server) => server.id === test.device_id)

  return (
    <tr>
      <td>{day}</td>
      <td>
        <a href={hrefOf(`easeedesk/ticket/${test.ticket_id}`) ?? '#'}>#{test.ticket_id}</a>
      </td>
      {canEdit ? (
        <>
          <td>
            <select className="form-control" value={device} onChange={(e) => setDevice(e.target.value)} aria-label={`Restored server, test of ${day}`}>
              <option value="">-</option>
              {!known && test.device_id !== null && <option value={test.device_id}>{test.device_name}</option>}
              {servers.map((server) => (
                <option key={server.id} value={server.id}>
                  {server.name}
                </option>
              ))}
            </select>
          </td>
          <td>
            <input
              className="form-control"
              type="number"
              min={1}
              step={1}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              aria-label={`Minutes the restore took, test of ${day}`}
            />
          </td>
          <td>
            <button type="button" className="btn btn-primary" disabled={!dirty || busy} onClick={save} aria-label={`Save result, test of ${day}`}>
              {busy ? 'Saving...' : 'Save'}
            </button>
            {saved && !error && <span className="text-success"> Result saved.</span>}
            {error && <span className="text-danger"> {error}</span>}
          </td>
        </>
      ) : (
        <>
          <td>{test.device_name || '-'}</td>
          <td>{test.duration_minutes ?? '-'}</td>
          <td></td>
        </>
      )}
    </tr>
  )
}
